import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";
import { Box, ClickAwayListener } from "@mui/material";
import { Color } from "@tiptap/extension-color";
import Heading from "@tiptap/extension-heading";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import classNames from "classnames";
import { not } from "ramda";
import React, { createElement, useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";

import { Spacing } from "../types/enum";
import { TEXT_COLOR_OPTIONS } from "./consts";

/**
 * Types
 */
interface Props {
  [key: string]: any;
  children?: any;
  className?: string;
  value?: string;
  onChange: (value: string) => void;
}

/**
 * Constants
 */
const TEXT_SIZE_OPTIONS = [
  { label: "Normal", value: "paragraph" },
  { label: "Heading 1", value: "heading", level: 1 },
  { label: "Heading 2", value: "heading", level: 2 },
  { label: "Heading 3", value: "heading", level: 3 },
  { label: "Heading 4", value: "heading", level: 4 },
];

const TEXT_ALIGN_OPTIONS = [
  { label: "Left", value: "left", icon: FormatAlignLeftIcon },
  { label: "Center", value: "center", icon: FormatAlignCenterIcon },
  { label: "Right", value: "right", icon: FormatAlignRightIcon },
  { label: "Justify", value: "justify", icon: FormatAlignJustifyIcon },
];

/**
 * Styles
 */
const useStyles = makeStyles()((theme) => ({
  root: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.action.disabled}`,
    position: "relative",
    "& .ProseMirror": {
      color: theme.palette.common.black,
      backgroundColor: theme.palette.common.white,
      paddingTop: theme.spacing(Spacing.sm),
      paddingBottom: theme.spacing(Spacing.sm),
      paddingLeft: theme.spacing(Spacing.sm),
      paddingRight: theme.spacing(Spacing.sm),
      fontSize: theme.typography.h5.fontSize,
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      minHeight: "200px",
      outline: "none",
      "& a": {
        textDecoration: "none",
        color: "#D0722A !important",
      },
      "& h1": {
        fontSize: "40px",
        fontWeight: 500,
        lineHeight: "72px",
        [theme.breakpoints.down("md")]: {
          lineHeight: "58px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: "32px",
          lineHeight: "36px",
        },
      },
      "& h2": {
        fontSize: "40px",
        fontWeight: 400,
        lineHeight: "60px",
        [theme.breakpoints.down("sm")]: {
          fontSize: "32px",
          lineHeight: "48px",
        },
      },
      "& h3": {
        fontSize: "32px",
        fontWeight: 400,
        lineHeight: "58px",
        [theme.breakpoints.down("sm")]: {
          fontSize: "24px",
          lineHeight: "36px",
        },
      },
      "& h4": {
        fontSize: "24px",
        fontWeight: 400,
        lineHeight: "36px",
        [theme.breakpoints.down("sm")]: {
          fontSize: "24px",
          lineHeight: "36px",
        },
      },
      "& p": {
        marginBottom: theme.spacing(Spacing.sm),
        fontSize: "18px",
        fontWeight: 300,
        lineHeight: "28px",
      },
      "& ul": {
        padding: "unset",
        marginLeft: theme.spacing(Spacing.ml),
      },
      "& li": {
        fontSize: "18px",
        fontWeight: 300,
        lineHeight: "28px",
        paddingLeft: `${theme.spacing(Spacing.s)} !important`,
        "& ::marker": {
          fontSize: "24px",
        },
      },
    },
  },
  toolbar: {
    borderLeft: "none",
    borderTop: "none",
    borderRight: "none",
    borderBottom: `1px solid ${theme.palette.action.disabled}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(Spacing.xs),
    padding: theme.spacing(Spacing.xs, Spacing.sm),
    backgroundColor: theme.palette.background.default,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
  toolbarButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: theme.spacing(Spacing.xs),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.action.active,
    fontSize: "14px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "28px",
    minHeight: "28px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.is-active": {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  textSizeSelect: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: theme.spacing(Spacing.xs),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.action.active,
    fontSize: "14px",
    minWidth: "80px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:focus": {
      outline: "none",
      backgroundColor: theme.palette.action.hover,
    },
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    padding: theme.spacing(Spacing.xs),
    zIndex: 1000,
    minWidth: "100px",
  },
  dropdownItem: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: theme.spacing(Spacing.xs),
    borderRadius: theme.shape.borderRadius,
    fontSize: "14px",
    width: "100%",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&.is-active": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },
  textColorDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    padding: theme.spacing(Spacing.xs),
    zIndex: 1000,
    width: "fit-content",
  },
  colorSwatchGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: theme.spacing(Spacing.xs),
    marginBottom: theme.spacing(Spacing.xs),
  },
  colorSwatch: {
    width: "20px",
    height: "20px",
    cursor: "pointer",
    "&.is-active": {
      border: `2px solid white`,
    },
  },
  linkDialog: {
    position: "absolute",
    top: "100%",
    left: 0,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[4],
    padding: theme.spacing(Spacing.s),
    zIndex: 1000,
    minWidth: "300px",
  },
  linkInput: {
    width: "100%",
    padding: theme.spacing(Spacing.xs),
    border: `1px solid ${theme.palette.action.disabled}`,
    borderRadius: theme.shape.borderRadius,
    fontSize: "14px",
    marginBottom: theme.spacing(Spacing.xs),
    "&:focus": {
      outline: "none",
      borderColor: theme.palette.primary.main,
    },
  },
  linkButtons: {
    display: "flex",
    gap: theme.spacing(Spacing.m),
  },
  linkButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: theme.spacing(Spacing.xs),
    borderRadius: theme.shape.borderRadius,
    fontSize: "14px",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const RichTextEditor: React.FC<Props> = ({ className, value, children, onChange, ...rest }) => {
  const { classes, cx } = useStyles();
  const [showTextSizeDropdown, setShowTextSizeDropdown] = useState(false);
  const [showTextAlignDropdown, setShowTextAlignDropdown] = useState(false);
  const [showTextColorDropdown, setShowTextColorDropdown] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [selectedColor, setSelectedColor] = useState("#605E5F");
  const [_updateTrigger, setUpdateTrigger] = useState(0);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        link: false,
        underline: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer nofollow",
        },
        validate: (href) => /^https?:\/\//.test(href),
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      TextStyle,
      Color.configure({
        types: ["textStyle"],
      }),
      Underline,
      Heading.configure({
        levels: [1, 2, 3, 4],
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        spellcheck: "false",
      },
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const currentContent = editor.getHTML();
      const newContent = value || "";
      if (currentContent !== newContent) {
        editor.commands.setContent(newContent);
      }
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const updateSelectedColor = () => {
      const attrs = editor.getAttributes("textStyle") as { color?: string };
      setSelectedColor(attrs?.color || "#605E5F");
    };
    updateSelectedColor();
    editor.on("selectionUpdate", updateSelectedColor);
    editor.on("transaction", updateSelectedColor);
    return () => {
      editor.off("selectionUpdate", updateSelectedColor);
      editor.off("transaction", updateSelectedColor);
    };
  }, [editor]);

  const getCurrentTextSize = useCallback(() => {
    if (not(editor)) return "Normal";

    if (editor.isActive("paragraph")) return "Normal";
    for (let i = 1; i <= 4; i++) {
      if (editor.isActive("heading", { level: i })) {
        return `Heading ${i}`;
      }
    }
    return "Normal";
  }, [editor]);

  const setTextSize = useCallback(
    (option: { value: string; level?: number }) => {
      if (editor) {
        if (option.value === "paragraph") {
          editor.chain().focus().setParagraph().run();
        } else if (option.value === "heading" && option.level) {
          editor
            .chain()
            .focus()
            .toggleHeading({ level: option.level as 1 | 2 | 3 | 4 })
            .run();
        }
      }
      setShowTextSizeDropdown(false);
    },
    [editor],
  );

  const getCurrentTextAlign = useCallback(() => {
    if (not(editor)) return FormatAlignLeftIcon;

    if (editor.isActive({ textAlign: "center" })) return FormatAlignCenterIcon;
    if (editor.isActive({ textAlign: "right" })) return FormatAlignRightIcon;
    if (editor.isActive({ textAlign: "justify" })) return FormatAlignJustifyIcon;
    return FormatAlignLeftIcon;
  }, [editor]);

  const getCurrentTextAlignValue = useCallback(() => {
    if (not(editor)) return "Left";

    if (editor.isActive({ textAlign: "center" })) return "Center";
    if (editor.isActive({ textAlign: "right" })) return "Right";
    if (editor.isActive({ textAlign: "justify" })) return "Justify";
    return "Left";
  }, [editor]);

  const setTextAlign = useCallback(
    (alignment: string) => {
      if (editor) {
        const isCurrentlyActive = editor.isActive({ textAlign: alignment });

        if (isCurrentlyActive) {
          editor.chain().focus().unsetTextAlign().run();
        } else {
          editor.chain().focus().setTextAlign(alignment).run();
        }
        setUpdateTrigger((prev) => prev + 1);
      }
      setShowTextAlignDropdown(false);
    },
    [editor],
  );

  const setTextColor = useCallback(
    (color: string) => {
      if (!editor) return;
      setSelectedColor(color);
      editor.chain().focus().setColor(color).run();
      setUpdateTrigger((prev) => prev + 1);
      setShowTextColorDropdown(false);
    },
    [editor],
  );

  const clearTextColor = useCallback(() => {
    if (!editor) return;
    setSelectedColor("#605E5F");
    editor.chain().focus().unsetColor().run();
    setUpdateTrigger((prev) => prev + 1);
    setShowTextColorDropdown(false);
  }, [editor]);

  const openLinkDialog = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href;
    setLinkUrl(previousUrl || "");
    setShowLinkDialog(true);
    editor.chain().focus().run();
  }, [editor]);

  const saveLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      if (hasSelection) {
        editor.chain().focus().setLink({ href: linkUrl }).setTextSelection(to).run();
      } else {
        editor.chain().focus().insertContent(`<a href="${linkUrl}">${linkUrl}</a> `).run();
      }
    }
    setShowLinkDialog(false);
    setLinkUrl("");
  }, [editor, linkUrl]);

  const cancelLink = useCallback(() => {
    setShowLinkDialog(false);
    setLinkUrl("");
  }, []);

  if (not(editor)) {
    return null;
  }

  return (
    <Box className={classNames(classes.root, className)} {...rest}>
      <div ref={toolbarRef} className={classes.toolbar}>
        {/* Text Color */}
        <Box style={{ position: "relative" }}>
          <button
            type="button"
            className={classes.toolbarButton}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowLinkDialog(false);
              setShowTextSizeDropdown(false);
              setShowTextAlignDropdown(false);
              setShowTextColorDropdown(!showTextColorDropdown);
            }}
            title="Text color"
          >
            <span
              style={{
                color: selectedColor,
                fontWeight: 700,
                WebkitTextStroke: "1px #fff",
                paintOrder: "stroke fill",
              }}
            >
              A
            </span>
          </button>

          {showTextColorDropdown && (
            <ClickAwayListener onClickAway={() => setShowTextColorDropdown(false)}>
              <Box className={classes.textColorDropdown}>
                <Box className={classes.colorSwatchGrid}>
                  {TEXT_COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`${classes.colorSwatch} ${
                        selectedColor.toLowerCase() === color.toLowerCase() ? "is-active" : ""
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setTextColor(color);
                      }}
                      style={{
                        backgroundColor: color,
                      }}
                      title={color}
                    />
                  ))}
                </Box>
                <button type="button" className={classes.linkButton} onClick={clearTextColor}>
                  Clear
                </button>
              </Box>
            </ClickAwayListener>
          )}
        </Box>

        {/* Text Size Dropdown */}
        <Box style={{ position: "relative" }}>
          <button
            type="button"
            className={classes.textSizeSelect}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowLinkDialog(false);
              setShowTextSizeDropdown(!showTextSizeDropdown);
              setShowTextAlignDropdown(false);
              setShowTextColorDropdown(false);
            }}
          >
            {getCurrentTextSize()}
          </button>

          {showTextSizeDropdown && (
            <ClickAwayListener onClickAway={() => setShowTextSizeDropdown(false)}>
              <Box className={classes.dropdown}>
                {TEXT_SIZE_OPTIONS.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className={cx(classes.dropdownItem, {
                      "is-active": getCurrentTextSize() === option.label,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTextSize(option);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </Box>
            </ClickAwayListener>
          )}
        </Box>

        {/* Bold */}
        <button
          type="button"
          className={cx(classes.toolbarButton, { "is-active": editor.isActive("bold") })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleBold().run();
            setUpdateTrigger((prev) => prev + 1);
          }}
        >
          B
        </button>

        {/* Underline */}
        <button
          type="button"
          className={cx(classes.toolbarButton, { "is-active": editor.isActive("underline") })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleUnderline().run();
            setUpdateTrigger((prev) => prev + 1);
          }}
        >
          U
        </button>

        {/* Italic */}
        <button
          type="button"
          className={cx(classes.toolbarButton, { "is-active": editor.isActive("italic") })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleItalic().run();
            setUpdateTrigger((prev) => prev + 1);
          }}
        >
          I
        </button>

        {/* Text Align Dropdown */}
        <Box style={{ position: "relative" }}>
          <button
            type="button"
            className={classes.textSizeSelect}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowLinkDialog(false);
              setShowTextAlignDropdown(!showTextAlignDropdown);
              setShowTextSizeDropdown(false);
              setShowTextColorDropdown(false);
            }}
          >
            {createElement(getCurrentTextAlign(), { style: { fontSize: "16px" } })}
          </button>

          {showTextAlignDropdown && (
            <ClickAwayListener onClickAway={() => setShowTextAlignDropdown(false)}>
              <Box className={classes.dropdown}>
                {TEXT_ALIGN_OPTIONS.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className={cx(classes.dropdownItem, {
                      "is-active": getCurrentTextAlignValue() === option.label,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTextAlign(option.value);
                    }}
                  >
                    <option.icon style={{ fontSize: "16px" }} />
                  </button>
                ))}
              </Box>
            </ClickAwayListener>
          )}
        </Box>

        {/* Ordered List */}
        <button
          type="button"
          className={cx(classes.toolbarButton, { "is-active": editor.isActive("orderedList") })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleOrderedList().run();
            setUpdateTrigger((prev) => prev + 1);
          }}
        >
          <FormatListNumberedIcon style={{ fontSize: "16px" }} />
        </button>

        {/* Bullet List */}
        <button
          type="button"
          className={cx(classes.toolbarButton, { "is-active": editor.isActive("bulletList") })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleBulletList().run();
            setUpdateTrigger((prev) => prev + 1);
          }}
        >
          <FormatListBulletedIcon style={{ fontSize: "16px" }} />
        </button>

        {/* Link */}
        <Box style={{ position: "relative" }}>
          <button
            type="button"
            className={cx(classes.toolbarButton, { "is-active": editor.isActive("link") })}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowTextSizeDropdown(false);
              setShowTextAlignDropdown(false);
              setShowTextColorDropdown(false);
              openLinkDialog();
            }}
          >
            <LinkIcon style={{ fontSize: "16px" }} />
          </button>

          {showLinkDialog && (
            <ClickAwayListener onClickAway={cancelLink}>
              <Box className={classes.linkDialog}>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="Enter link URL"
                  className={classes.linkInput}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      saveLink();
                    } else if (e.key === "Escape") {
                      cancelLink();
                    }
                  }}
                />
                <Box className={classes.linkButtons}>
                  <button type="button" className={classes.linkButton} onClick={saveLink}>
                    Save
                  </button>
                  <button type="button" className={classes.linkButton} onClick={cancelLink}>
                    Cancel
                  </button>
                </Box>
              </Box>
            </ClickAwayListener>
          )}
        </Box>
      </div>

      <EditorContent editor={editor} />
      {children}
    </Box>
  );
};

export default RichTextEditor;
